import {
  FC,
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  TransitionEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { PluginContext } from 'ts-components/Plugins/context';
import {
  AI_PANEL_MIN_WIDTH,
  clampPanelWidth,
  keyboardResize,
  maxPanelWidth,
  readStoredPanelWidth,
  writeStoredPanelWidth,
} from './panelWidth';
import {
  AI_MOTION_MS,
  AI_MOTION_SETTLE_SLACK_MS,
  motionTransition,
  panelTransitionInitial,
  panelTransitionReducer,
  usePrefersReducedMotion,
} from './motion';
import { gridFrame, gridFrameVars } from './gridFrame';

export const AI_PANEL_Z_INDEX = 95;

const RESIZE_HANDLE_Z_INDEX = 10;

const GRID_LEFT_MARGIN = 40; // half the 80px BreakpointContainer leaves at XL
const GRID_PANEL_GAP = 24;

const panelViewportWidth = () => document.documentElement.clientWidth;

export const measureGridShift = (
  root: HTMLElement,
  viewportWidth: number,
  panelWidth: number
): number => {
  const grid = root.querySelector<HTMLElement>('.breakpoint-container');
  if (!grid) return 0;
  const layoutLeft = (element: HTMLElement) => {
    let left = 0;
    for (
      let node: HTMLElement | null = element;
      node;
      node = node.offsetParent as HTMLElement | null
    ) {
      left += node.offsetLeft;
    }
    return left;
  };
  const gridLeft = layoutLeft(grid);
  const gridRight = gridLeft + grid.offsetWidth;
  const panelLeft = viewportWidth - panelWidth;
  const neededShift = Math.max(0, gridRight + GRID_PANEL_GAP - panelLeft);
  const fullShift = Math.max(0, gridLeft - layoutLeft(root) - GRID_LEFT_MARGIN);
  return Math.round(Math.min(neededShift, fullShift));
};

const LayoutRoot = styled.div<{ $panelMounted: boolean }>`
  position: relative;
  width: 100%;

  ${({ $panelMounted }) =>
    $panelMounted
      ? css`
          .breakpoint-container {
            transform: var(--ai-grid-transform, none);
            transition: var(--ai-grid-transition, none);
          }
        `
      : ''}
  ${({ $panelMounted }) => gridFrame($panelMounted)}
`;

const PanelRail = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  max-width: 100vw;
  z-index: ${AI_PANEL_Z_INDEX};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid var(--color-border-medium);
  background: var(--color-background-white);
  box-shadow: -6px 0 16px
    color-mix(in srgb, var(--color-background-black) 4%, transparent);
`;

const ResizeHandle = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  width: 8px;
  z-index: ${RESIZE_HANDLE_Z_INDEX};
  cursor: col-resize;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 3px;
    width: 2px;
    background: ${({ $active }) =>
      $active ? 'var(--color-border-active)' : 'transparent'};
    transition: background 0.1s;
  }

  &:hover::after,
  &:focus-visible::after {
    background: var(--color-border-active);
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const RAIL_INERT_PROPS = { inert: '' } as Record<string, unknown>;

export type PiChatSideLayoutProps = {
  panel: ReactNode | null;
  children: ReactNode;
};

const measureViewportTop = (element: HTMLElement) =>
  Math.max(0, Math.round(element.getBoundingClientRect().top));

export const PiChatSideLayout: FC<PiChatSideLayoutProps> = ({
  panel,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [panelTop, setPanelTop] = useState(0);
  const [gridShift, setGridShift] = useState(0);
  const { suppressOverlays } = useContext(PluginContext);
  const reducedMotion = usePrefersReducedMotion();
  const { t } = useTranslation();

  const [width, setWidth] = useState(() =>
    readStoredPanelWidth(window.innerWidth)
  );
  const widthRef = useRef(width);
  widthRef.current = width;
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    latest: number;
    frame: number;
  } | null>(null);

  const handleResizePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: widthRef.current,
      latest: widthRef.current,
      frame: 0,
    };
    setResizing(true);
  };

  const handleResizePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.latest = clampPanelWidth(
      drag.startWidth + (drag.startX - event.clientX),
      window.innerWidth
    );
    if (drag.frame) return;
    drag.frame = requestAnimationFrame(() => {
      drag.frame = 0;
      setWidth(drag.latest);
    });
  };

  const endResize = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    cancelAnimationFrame(drag.frame);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setWidth(drag.latest);
    setResizing(false);
    writeStoredPanelWidth(drag.latest);
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = keyboardResize(
      event.key,
      event.shiftKey,
      widthRef.current,
      window.innerWidth
    );
    if (next === null) return;
    event.preventDefault();
    setWidth(next);
    writeStoredPanelWidth(next);
  };

  useEffect(() => {
    if (!resizing) return undefined;
    const { style } = document.body;
    const previous = style.userSelect;
    style.userSelect = 'none';
    return () => {
      style.userSelect = previous;
    };
  }, [resizing]);

  const wanted = Boolean(panel);
  const [transition, dispatch] = useReducer(
    panelTransitionReducer,
    wanted,
    panelTransitionInitial
  );
  if (wanted !== transition.wanted) {
    dispatch({ type: wanted ? 'show' : 'hide', reducedMotion });
  }
  const [lastPanel, setLastPanel] = useState<ReactNode>(panel);
  if (panel && panel !== lastPanel) setLastPanel(panel);
  if (!panel && !transition.mounted && lastPanel) setLastPanel(null);
  const mounted = transition.mounted;
  const shownPanel = panel ?? lastPanel;

  const awaitingFrame = mounted && transition.wanted && !transition.visible;
  useEffect(() => {
    if (!awaitingFrame) return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => dispatch({ type: 'frame' }));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [awaitingFrame]);

  const exiting = mounted && !transition.wanted;
  useEffect(() => {
    if (!exiting) return undefined;
    const timer = setTimeout(
      () => dispatch({ type: 'exitEnd' }),
      AI_MOTION_MS + AI_MOTION_SETTLE_SLACK_MS
    );
    return () => clearTimeout(timer);
  }, [exiting]);

  const handleRailTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === 'transform'
    ) {
      dispatch({ type: 'exitEnd' });
    }
  };

  useEffect(() => {
    if (!mounted) return undefined;
    return suppressOverlays();
  }, [mounted, suppressOverlays]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!mounted || !root) return undefined;
    const observer = new ResizeObserver(() => update());
    let observedGrid: Element | null = null;
    const update = () => {
      setPanelTop(measureViewportTop(root));
      const grid = root.querySelector('.breakpoint-container');
      if (grid !== observedGrid) {
        if (observedGrid) observer.unobserve(observedGrid);
        if (grid) observer.observe(grid);
        observedGrid = grid;
      }
      const fitted = clampPanelWidth(widthRef.current, window.innerWidth);
      setWidth(fitted);
      setGridShift(measureGridShift(root, panelViewportWidth(), fitted));
    };
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setPanelTop(measureViewportTop(root));
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [mounted]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!mounted || !root) return;
    setGridShift(measureGridShift(root, panelViewportWidth(), width));
  }, [mounted, width]);

  const animate = motionTransition(['transform'], reducedMotion || resizing);
  const shift = transition.visible ? gridShift : 0;

  return (
    <LayoutRoot
      ref={rootRef}
      $panelMounted={mounted}
      style={
        mounted
          ? ({
              '--ai-grid-transform': shift ? `translateX(${-shift}px)` : 'none',
              '--ai-grid-transition': animate,
              ...gridFrameVars(transition.visible, reducedMotion),
            } as Record<string, string>)
          : undefined
      }
    >
      {children}
      {mounted ? (
        <PanelRail
          style={{
            top: panelTop,
            width,
            transform: transition.visible ? 'none' : 'translateX(100%)',
            transition: animate,
          }}
          onTransitionEnd={handleRailTransitionEnd}
          data-qa-id="pi-chat-rail"
          data-qa-visible={transition.visible}
          {...(transition.wanted ? {} : RAIL_INERT_PROPS)}
        >
          <ResizeHandle
            role="separator"
            aria-orientation="vertical"
            aria-label={t('Resize AI panel')}
            aria-valuenow={width}
            aria-valuemin={AI_PANEL_MIN_WIDTH}
            aria-valuemax={maxPanelWidth(window.innerWidth)}
            tabIndex={0}
            $active={resizing}
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            onLostPointerCapture={endResize}
            onKeyDown={handleResizeKeyDown}
            data-qa-id="pi-chat-resize-handle"
          />
          {shownPanel}
        </PanelRail>
      ) : null}
    </LayoutRoot>
  );
};
